import { GeomUtils } from "@esWebAPI/es/util/GeomUtils";
import {AzureDragPointHandler} from "../handler/AzureDragPointHandler";
import { EditPolygonHandler } from "../handler/EditPolygonHandler";
import History from "@esWebAPI/es/util/history";

import type { Polygon } from "geojson";
import type {AzureBaseMap} from "./AzureBaseMap";
import type {AzureSelect} from "./AzureSelect";
import type {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import type { AzureOverlay } from "./AzureOverlay";
import {CoordinatesGeoLocationMeta} from "@esWebAPI/es/model/Layer";

export class AzureEdit {

    private readonly baseMapModule: AzureBaseMap;
    private readonly featureViewModel: FeatureViewModel;
    private readonly selectModule: AzureSelect;
    private readonly overlayModule: AzureOverlay;
    private readonly eventChannel: any;
    private drawHandler: AzureDragPointHandler|undefined;
    private editPolygonHandler: EditPolygonHandler | null;
    private history = new History(null, this.updateFromHistory);
    private isLocationUnlocked: boolean = false;
    private idSequence = -1;

    constructor(baseMapModule: AzureBaseMap, featureViewModel: FeatureViewModel, selectModule: AzureSelect, overlayModule: AzureOverlay, eventChannel: any) {
        this.baseMapModule = baseMapModule;
        this.featureViewModel = featureViewModel;
        this.selectModule = selectModule;
        this.overlayModule = overlayModule;
        this.eventChannel = eventChannel;
        this.editPolygonHandler = null;
    }

    private updateFromHistory() {
        //const actions = this.getActions(); //build compressed edit list
        //this.hideEditFeatures(actions);
        //this.showEditFeatures(actions);
    }

     //Build compressed edit list - edits on the same feature should be compressed into a single edit
    private getActions() {
        const compressedActions = [];
        const actionMap: any = {};
        for (let h = 1; h <= this.history.index; h++) {
            const state = this.history.states[h];
            const newEdit = state.edit;
            const oldAction = actionMap[state.featureKey];
            if (oldAction) { // edit already exists for feature
                const oldEdit = oldAction.edit;
                switch (newEdit.$type) {
                    case "Update":
                        if (typeof newEdit.geom !== "undefined") {
                            oldAction.edit.geom = newEdit.geom;
                            oldAction.geom = newEdit.geom;
                        }
                        if (newEdit.attrs) {
                            if (oldEdit.attrs) {
                                for (const attrName in newEdit.attrs) {
                                    oldEdit.attrs[attrName] = newEdit.attrs[attrName];
                                }
                            } else {
                                oldEdit.attrs = $.extend({}, newEdit.attrs);
                            }
                        }
                        if (oldAction.edit.$type === "Remove") {
                            oldAction.edit.$type = "Update";
                        }
                        break;
                    case "Remove":
                        $.extend(oldEdit, newEdit);
                        delete oldEdit.geom;
                        delete oldEdit.attrs;
                        break;
                    default:
                        // should not be possible
                        throw new Error("IllegalState:" + state.edit.$type);
                }
            } else { // No edit exists for feature
                const action = es.util.object.deepCopy(state); // defensive copy
                actionMap[action.featureKey] = action;
                compressedActions.push(action);
            }
        }
        return compressedActions;
    }

    private beginDragPointsForAction(action: any) {
        this.cancelDrawing();
        //handlerFeatureKeys[action.featureKey] = 1;
        //showEditFeatures(getActions());
        const label = action.feature ? action.feature.label : null;

        //const key = action.featureKey.split(":");
        //const layerViewId = parseInt(key[0]);


        this.drawHandler = new AzureDragPointHandler(this.baseMapModule, action.geom, label, action.feature.style);

        this.onLocationLock = this.onLocationLock.bind(this);
        this.eventChannel.addListener("onLocationLock", this.onLocationLock);

        const onDragEnd = (geom: any) => {
            const key = action.featureKey.split(":");
            const isEditing = this.selectModule.isInEditMode(parseInt(key[0]), parseInt(key[1])) ||
                action.edit.$type === "Create";
            if (isEditing) {
                this.updateGeom(action, geom).done((action: any) => {
                    this.eventChannel.fireEvent("onUpdateLocation", action);
                });
            } else {
                this.update(parseInt(key[0]), parseInt(key[1]), geom);
            }
        };
        this.drawHandler.eventChannel.addListener("onDragEnd", onDragEnd);
        const onDragStart = () => {
            const key = action.featureKey.split(":");
            const isEditing = this.selectModule.isInEditMode(parseInt(key[0]), parseInt(key[1])) ||
                action.edit.$type === "Create";
            this.eventChannel.fireEvent(isEditing ? "onBeginUpdateFeature" : "onBeginEditFeature",
                parseInt(key[0]), parseInt(key[1]));
        };
        this.drawHandler.eventChannel.addListener("onDragStart", onDragStart);

        this.drawHandler.eventChannel.addOnce("onDestroy", () => {
            //delete handlerFeatureKeys[action.featureKey];
            this.drawHandler.eventChannel.removeListener("onDragStart", onDragStart);
            this.drawHandler.eventChannel.removeListener("onDragEnd", onDragEnd);
            this.eventChannel.removeListener("onLocationLock", this.onLocationLock);
            action = null;
            this.drawHandler = undefined;
        });
    }

    private beginEditPolygon(action) {
        const geojsonGeom = GeomUtils.serverToGeoJson(action.geom) as Polygon;
        this.editPolygonHandler = new EditPolygonHandler(this.baseMapModule, this.overlayModule, geojsonGeom);
        const key = action.featureKey.split(":");
        this.eventChannel.fireEvent("onBeginEditFeature", +key[0], +key[1]);

        this.editPolygonHandler.eventChannel.addListener("onGeomChanged", () => {
            const geom = this.editPolygonHandler!.geom;
            const serverGeom = GeomUtils.geoJsonToServer(geom);
            this.updateGeom(action, serverGeom);
        });
    }

    private updateGeom(action: any, geom: any) {
        const point = geom.slice(1);
        const layerView = this.featureViewModel.getLayerById(action.edit.layerViewId);
        const geoLocationMeta = layerView.getGeoLocationMetaObjRef();

        if (!this.isLocationUnlocked || !geoLocationMeta) {
            return this.update(action.edit.layerViewId, action.edit.featureId, geom);
        }

        // We have an address, so attempt a reverse geocode
        if (geoLocationMeta.$type === "AddressGeoLocationMeta") {
            const updateAddressAttrs = (addressStr: string, addressDetail: any) => {
                let newAttrs: any;
                if (layerView.getType() === "MapScopeCustomPointLayerView") {
                    newAttrs = [];
                    newAttrs["ADDRESS"] = addressStr;
                } else {
                    newAttrs = this.getAttributesFromAddress(geoLocationMeta.attrNames, addressDetail);
                }

                return this.update(action.edit.layerViewId, action.edit.featureId, geom, newAttrs);
            };

            const geocodeDfd = $.Deferred();
            es.api.geocode.reverseGeocode(point).done((addressStr: string, addressDetail: any) => {
                geocodeDfd.resolve(addressStr, addressDetail);
            }).fail(() => {
                //If geocode failed, we silently continue...
                geocodeDfd.resolve('', {'addressTypes': [], 'address': ''});
            });

            return geocodeDfd.then((addressStr: string, addressDetail: any) =>
                updateAddressAttrs(addressStr, addressDetail)
            );
        }

        if (geoLocationMeta.$type === "CoordinatesGeoLocationMeta") {
            const xyGeoLocationMeta =  geoLocationMeta as CoordinatesGeoLocationMeta;
            const point = [geom[1], geom[2]];

            const xAttr = layerView.getLayerInfo().attributes.get('name', xyGeoLocationMeta.xAttr);
            const yAttr = layerView.getLayerInfo().attributes.get('name', xyGeoLocationMeta.yAttr);

            if (xAttr && xAttr.attrType == 'INTEGER') {
                point[0] = Math.round(point[0]);
            }
            if (yAttr && yAttr.attrType == 'INTEGER') {
                point[1] = Math.round(point[1]);
            }

            // todo: we really need to fix this inconsistency but too close to a release to do now
            const newAttrs = action.edit?.attrs || action.feature?.attrs;
            if (xAttr) newAttrs[xyGeoLocationMeta.xAttr] = point[0];
            if (yAttr) newAttrs[xyGeoLocationMeta.yAttr] = point[1];

            return this.update(action.edit.layerViewId, action.edit.featureId, geom, newAttrs);
        }

        if (geoLocationMeta.$type === "CopyLocationsGeoLocationMeta") {
            return this.update(action.edit.layerViewId, action.edit.featureId, geom);
        }
    }

    private getAttributesFromAddress(geoFields: any, addressDetail: any) {
        const newAddress = [];
        const newAttrs = [];
        const fieldNames = geoFields.slice();
        while (newAddress.length < fieldNames.length) {
            newAddress.push("");
        }
        const types = addressDetail.addressTypes;
        const address = addressDetail.address;

        for (let j = 0; j < types.length; j++) {
            let tempType = es.api.geocode.getAddressComponent(types[j]);
            if (!tempType) {
                tempType = [types[j]];
            }
            for (let k = 0; k < tempType.length; k++) {
                var position;
                if ((position = this.isContain(fieldNames, tempType[k])) !== -1) {
                    newAddress[position] = address[j];
                    break;
                }
            }
        }

        let length = fieldNames.length;

        while (length--) {
            const newValue = newAddress[length];
            newAttrs[fieldNames[length]] = newValue;
        }
        return newAttrs;
    }

    private isContain(fields: any, addressType: any) {
        for (let j = 0; j < fields.length; j++) {
            //TODO - FIXME: This will fail to complete addresses if User selects an address field on Upload that has a
            // weird name.
            if (fields[j].toLowerCase().includes(addressType.toLowerCase())) {
                return j;
            }
        }
        return -1;
    }

    public getAction(layerViewId: number, featureId: number) {
        const featureKey = layerViewId + ":" + featureId;
        const actions = this.getActions();
        for (let a = actions.length; a-- > 0;) {
            if (actions[a].featureKey === featureKey) {
                return actions[a];
            }
        }
        return null;
    }

    public hasAction(layerViewId: number, featureId: number) {
        return this.getAction(layerViewId, featureId) != null;
    }

    public cancelDrawing() {
        if (this.drawHandler) {
            this.drawHandler.destroy();
        }

        if(this.editPolygonHandler){
            this.editPolygonHandler.destroy();
        }
    }

    public beginDragPoints(layerViewId: number, featureId: number) {
        this.cancelDrawing();
        const featureKey = layerViewId + ":" + featureId;

        const actions = this.getActions();
        for (let a = actions.length; a-- > 0;) {
            if (actions[a].featureKey === featureKey) {
                this.beginDragPointsForAction(actions[a]);
                return;
            }
        }
        this.update(layerViewId, featureId).done((action: any) => {
            // FIXME :: Where to put this?
            if(action.geom && (action.geom[0] === "PN" || action.geom[0] === "MP")) {
                this.beginEditPolygon(action);
                return;
            }
            this.beginDragPointsForAction(action)
        });
    }

    public beginPointDraw(layerViewId: number, attrs: any, featureId?: number) {
        this.cancelDrawing();
        const centerLocation = this.baseMapModule.getCenter();
        const geom = ["PT", centerLocation[0], centerLocation[1]];
        if (!featureId) {
            return this.create(layerViewId, geom, attrs);
        } else {
            return this.update(layerViewId, featureId, geom, attrs);
        }
    }

    public onLocationLock(unlocked: boolean) {
        this.isLocationUnlocked = unlocked;
    }

    public updatePoint(layerViewId: number, featureId: number, geom: any, attrs: any) {
        this.cancelDrawing();

        /* TODO
        if (!overlayIds[0]) {
            const style = this.featureViewModel.getLayerById(layerViewId).getStyleSettingsObjRef().defaultStyle;
            var pointId = overlayModule.add(geom, style);
            overlayIds.push(pointId);
            var removePoint = function (feature) {
                overlayModule.remove(pointId);
            };
            selectModule.getEventChannel().addOnce("onDeselectFeature", removePoint);
        } else {
            overlayModule.updateGeom(overlayIds[0], geom);
        }*/

        return this.update(layerViewId, featureId, geom, attrs).then((action: any) => {
            this.beginDragPoints(layerViewId, featureId);
            return action;
        });
    }

    public create(layerViewId: number, geom: any, attrs: any) {
        const featureId = this.idSequence--;
        const edit: any = {
            $type: "Create",
            featureId: featureId
        };
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (!layer) {
            throw new Error("Unknown layer : " + layerViewId);
        }
        let key: string;
        if (layerViewId > 0) {
            edit.layerViewId = layerViewId;
            key = layerViewId + ":" + featureId;
        } else {
            edit.layerMetaId = layer.getLayerMetaId();
            key = edit.layerMetaId + ":" + featureId;
        }
        if (geom) {
            edit.geom = geom;
        }
        if (attrs) {
            edit.attrs = attrs;
        }

        return es.map.vector.style.buildStyler(layer.getLayerView()).then((styler: any) => {
            const feature = {attrs: edit.attrs ? edit.attrs : {}, geom: edit.geom, layerViewId: layerViewId, id: featureId, style: {}};
            feature.style = es.map.vector.style.getStyle(styler, null, feature, layer.getLayerInfo()); // TODO FIXME Why do we need to set feature style?!
            const action = {
                featureKey: key,
                feature: feature,
                edit: edit,
                geom: edit.geom
            };
            this.history.add(action);
            this.eventChannel.fireEvent("onCreateFeature", action);
            this.eventChannel.fireEvent("onUpdateFeature", action);
            return action;
        });
    }

    public update(layerViewId: number, featureId: number, geom?: any, attrs?: any) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (!layer) {
            throw new Error("Unknown layer : " + layerViewId);
        }
        let key: string;
        if (layerViewId > 0) {
            key = layerViewId + ":" + featureId;
        } else {
            key = layer.getLayerMetaId() + ":" + featureId;
        }

        let dfdFeature;
        if (featureId > -1) {
            dfdFeature = layer.loadFeature(featureId);
        } else {
            //Assume there is already an edit action for this - probably a create
            const actions = this.getActions();
            let actionFeature = null;
            for (let a = 0; a < actions.length; a++) {
                if (actions[a].featureKey === key) {
                    actionFeature = actions[a].feature;
                    break;
                }
            }
            if (actionFeature !== null) {
                dfdFeature = es.util.deferred.resolved(actionFeature);
            } else {
                dfdFeature = es.util.deferred.rejected("Could not find previous edit action");
            }
        }

        return dfdFeature.then((feature: any) => {
            if (!feature) {
                return es.util.deferred.rejected("Unknown feature: "+layerViewId+":"+featureId);
            }

            const edit: any = {
                $type: "Update",
                featureId: featureId
            };
            if (layerViewId > 0) {
                edit.layerViewId = layerViewId;
            } else {
                edit.layerMetaId = layer.getLayerMetaId();
            }
            if (geom) {
                edit.geom = geom;
            }
            if (attrs) {
                edit.attrs = attrs;
            }
            const action = {
                featureKey: key,
                feature: feature,
                edit: edit,
                geom: edit.geom ? edit.geom : feature.geom
            };
            this.history.add(action);
            this.eventChannel.fireEvent("onUpdateFeature", action);
            this.eventChannel.fireEvent("onEditFeature", action);

            return action;
        });
    }

    public remove(layerViewId: number, featureIds: number[]) {
        this.cancelDrawing();
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (!layer) {
            throw new Error("Unknown layer : " + layerViewId);
        }
        let action: any;
        return layer.loadFeatures(featureIds).done((features: any[]) => {
            for (let s = features.length; s-- > 0;) {
                const feature = features[s];
                const edit: any = {
                    $type: "Remove",
                    featureId: feature.id
                };
                let key: string;
                if (layerViewId > 0) {
                    edit.layerViewId = layerViewId;
                    key = layerViewId + ":" + feature.id;
                } else {
                    edit.layerMetaId = layer.getLayerMetaId();
                    key = edit.layerMetaId + ":" + feature.id;
                }
                action = {
                    featureKey: key,
                    feature: feature,
                    edit: edit,
                    geom: feature.geom
                };
                this.history.add(action);

                this.eventChannel.fireEvent("onRemoveFeature", action);
                this.eventChannel.fireEvent("onEditFeature", action);
            }
        });
    }

    public commit() {
        this.cancelDrawing();
        const actions = this.getActions();
        const featureEdits: any[] = [];
        for (let a = 0; a < actions.length; a++) {
            featureEdits[a] = actions[a].edit;
        }
        return es.api.feature.edit(featureEdits).then((rs: any) => {
            //featureViewModel.setTimestamp(Date.now());
            //console.log(rs);
            this.history.clear();
            this.eventChannel.fireEvent("onCommitFeatureEdits", featureEdits, rs);
            return rs;
        });
    }

    public revert() {
        this.cancelDrawing();
        this.history.clear();
        this.eventChannel.fireEvent("onRevertFeatureEdits");
    }
}
